<!-- eslint-disable max-len -->
<template>
  <div class="sidebar-wrapper">
    <div
      class="sidebar"
      data-test="graphics-sidebarLeft"
      @click.self="deselectElement"
      @scroll="onScroll"
    >
      <div class="sidebar__top">
        <SidebarSettingsGroup data-test="graphics-AddElementsSection">
          <div>
            <button
              class="btn btn-style-a btn-sm width-full margin-bottom-sm"
              data-test="graphics-toggleAdvancedElementsMenu"
              data-entity="graphics-toggleAdvancedElementsMenu"
              @click="toggleElementLibrary"
            >
              <span class="far fa-layer-plus margin-right-xs" />Elements
            </button>
            <!-- Basic elements -->
            <h5 class="sidebar-settings-title">
              Quick Adds
            </h5>
            <div
              class="basic-element-list"
              data-test="graphics-basicElements"
            >
              <button
                data-balloon="Text"
                data-balloon-pos="up-left"
                data-test="graphics-addTextElement"
                data-entity="graphics-addTextElement"
                class="btn btn-sm btn-icon btn-style-b"
                @click="addElementOnClick('text')"
              >
                <span class="far fa-text-size" />
              </button>
              <button
                data-balloon="Image"
                data-balloon-pos="up-left"
                data-test="graphics-addImageElement"
                data-entity="graphics-addImageElement"
                class="btn btn-sm btn-icon btn-style-b"
                @click="pickAsset('image')"
              >
                <span class="far fa-image" />
              </button>
              <!-- Show Video Element only if user has permission, or T1 licensing is enabled -->
              <button
                v-if="showVideoElement"
                data-balloon="Video"
                data-balloon-pos="up-left"
                data-test="graphics-addVideoElement"
                data-entity="graphics-addVideoElement"
                class="btn btn-sm btn-icon btn-style-b"
                @click="pickAsset('video')"
              >
                <span class="far fa-video" />
              </button>
              <div class="shape-dropdown dropdown display-inline-block">
                <button
                  data-toggle="dropdown"
                  data-balloon="Shapes"
                  data-balloon-pos="up-left"
                  data-test="graphics-toggleShapesDropdown"
                  class="dropdown-btn btn btn-sm btn-icon btn-style-b"
                >
                  <span class="far fa-shapes" />
                  <span class="far fa-angle-down" />
                </button>
                <ul
                  class="dropdown-menu"
                  :style="dropdownMenuStyle"
                >
                  <li
                    class="dropdown-menu-item"
                    data-test="graphics-addRectangleElement"
                    data-entity="graphics-addRectangleElement"
                    aria-role="button"
                    @click="addElementOnClick('rectangle')"
                  >
                    <i class="fas fa-square" />
                    Rectangle
                  </li>
                  <li
                    class="dropdown-menu-item"
                    data-test="graphics-addOvalElement"
                    data-entity="graphics-addOvalElement"
                    aria-role="button"
                    @click="addElementOnClick('oval')"
                  >
                    <i class="fas fa-circle" />
                    Oval
                  </li>
                  <li
                    class="dropdown-menu-item"
                    data-test="graphics-addTriangleElement"
                    data-entity="graphics-addTriangleElement"
                    aria-role="button"
                    @click="addElementOnClick('triangle')"
                  >
                    <i class="fas fa-triangle" />
                    Triangle
                  </li>
                </ul>
              </div>
              <button
                data-balloon="QR Code"
                data-balloon-pos="up-right"
                data-test="graphics-addQRCodeElement"
                data-entity="graphics-addQRCodeElement"
                class="btn btn-sm btn-icon btn-style-b"
                @click="addElementOnClick('qrcode')"
              >
                <span class="fas fa-qrcode" />
              </button>
            </div>
          </div>
        </SidebarSettingsGroup>

        <SidebarSettingsGroup
          title="Layers"
          :title-style="{ marginBottom: '0rem' }"
          data-test="graphics-LayersSection"
          class="sidebar__layers-group"
        >
          <div class="sidebar-form-group sidebar-form-group--full">
            <layers
              v-if="getElementGroupMap.root"
              class="sidebar__layers"
              group-id="root"
              @modal="(opts) => $emit('modal', opts)"
            />

            <!-- Empty Message -->
            <div
              v-else
              class="sidebar-form-group sidebar-form-group--full"
              data-test="graphics-layers-empty"
            >
              <div class="layer font-style-italic empty-layer">
                Empty
              </div>
            </div>

            <div class="sidebar__add-element-cta">
              <button
                type="button"
                class="btn btn-style-b btn-sm sidebar__add-element-btn"
                data-test="graphics-addElementFloating"
                data-entity="graphics-addElementFloating"
                @click="openElementLibrary"
              >
                <span class="far fa-plus margin-right-xs" />
                Add Element
              </button>
            </div>
          </div>
        </SidebarSettingsGroup>
      </div>

      <div class="sidebar__bottom">
        <!-- Media Size Summary -->
        <SidebarSettingsGroup
          data-test="graphics-MediaSizeSummarySection"
          collapsible
          collapsed
        >
          <template #header="{ collapsible: isCollapsible }">
            <span class="media-size-summary__header-title flex-grow">Performance</span>
            <span :class="['media-size-summary__pill', isHeavy ? 'media-size-summary__pill--heavy' : 'media-size-summary__pill--optimal']">
              {{ isHeavy ? 'Heavy' : 'Optimal' }}
            </span>
            <span
              v-if="isCollapsible"
              class="fas fa-chevron-right"
            />
          </template>
          <div
            class="media-size-summary"
            data-test="graphics-mediaSizeSummary"
          >
            <div class="media-size-summary__total">
              {{ mediaSummaryTotalKnownBytes > 0 ? formatBytes(mediaSummaryTotalKnownBytes) : '\u2014' }}
              <div class="media-size-summary__subtext text-tiny">
                {{ isHeavy ? 'Likely to impact performance (5MB+)' : 'Optimal (0-4MB)' }}
              </div>
            </div>

            <div
              v-if="getMediaSizeSummary.staticImages.knownCount + getMediaSizeSummary.staticImages.unknownCount > 0"
              class="media-size-summary__row flex-space-between"
            >
              <span class="text-tiny">Images</span>
              <span class="text-tiny text-right">{{ formatMediaBucket(getMediaSizeSummary.staticImages) }}</span>
            </div>
            <div
              v-if="getMediaSizeSummary.videos.knownCount + getMediaSizeSummary.videos.unknownCount > 0"
              class="media-size-summary__row flex-space-between"
            >
              <span class="text-tiny">Videos</span>
              <span class="text-tiny text-right">{{ formatMediaBucket(getMediaSizeSummary.videos) }}</span>
            </div>
            <div
              v-if="getMediaSizeSummary.mappedMedia.knownCount + getMediaSizeSummary.mappedMedia.unknownCount > 0"
              class="media-size-summary__row flex-space-between"
            >
              <span class="text-tiny">Mapped</span>
              <span class="text-tiny text-right">{{ formatMediaBucket(getMediaSizeSummary.mappedMedia) }}</span>
            </div>
            <div
              v-if="isHeavy"
              class="media-size-summary__warning"
            >
              This graphic is large and may not be optimized for browser performance in Producer.
            </div>
          </div>
        </SidebarSettingsGroup>

        <SidebarSettingsGroup
          title="Snippets"
          data-test="graphics-SnippetsSection"
          collapsible
          collapsed
        >
          <div class="sidebar-form-group">
            <snippetpicker> Add snippets </snippetpicker>
            <snippet-list-item />
          </div>
        </SidebarSettingsGroup>
      </div>

      <!-- Data Element Setup Modals -->
    </div>

    <!-- Element Library -->
    <transition name="slide-in-to-right">
      <div
        v-if="showElementLibrary"
        class="element-library"
      >
        <div class="library-base">
          <div class="library-base__header">
            <span class="font-weight-bold text-normal">Elements</span>

            <div class="flex">
              <button
                type="button"
                class="btn btn-sm btn-icon btn-link"
                data-test="sidebarleft-button-pin"
                @click="libraryPinned = !libraryPinned"
              >
                <span
                  :class="
                    libraryPinned
                      ? 'fas fa-thumbtack text-normal'
                      : 'far fa-thumbtack text-normal fa-rotate-90'
                  "
                />
              </button>

              <button
                type="button"
                class="btn btn-sm btn-icon btn-link"
                data-test="sidebarleft-button-closelibrary"
                @click="toggleElementLibrary(true)"
              >
                <span class="fal fa-times text-normal" />
              </button>
            </div>
          </div>

          <div class="separator" />

          <div class="element-search padding-sm">
            <input
              v-model="elementMenuSearch"
              type="search"
              class="form-control element-search__input"
              placeholder="Search"
              data-test="graphics-advancedElementsSearch"
              data-entity="graphics-advancedElementsSearch"
            />
            <span class="fas fa-search element-search__icon" />

            <button
              v-if="activeLibraryTab === 'templates'"
              type="button"
              class="element-search__filter-btn btn btn-sm btn-icon btn-style-b"
              data-test="graphics-templatesFilterButton"
              @click.stop="toggleTemplateFilters"
            >
              <span class="far fa-bars-filter" />
            </button>

            <!-- Template Filters Dropdown -->
            <div
              v-if="showTemplateFilters && activeLibraryTab === 'templates'"
              v-click-outside="closeTemplateFilters"
              class="template-filters-dropdown"
              @click.stop
            >
              <div
                v-for="(items, facetKey) in templateFacets"
                :key="facetKey"
                class="facet-group"
              >
                <div class="facet-group-label">
                  {{ formatFacetLabel(facetKey) }}
                </div>
                <label
                  v-for="item in items"
                  :key="`${facetKey}-${item}`"
                  class="facet-option"
                >
                  <input
                    type="checkbox"
                    :checked="isFacetSelected(facetKey, item)"
                    @change="toggleFacet(facetKey, item)"
                  />
                  <span>{{ capitalizeWords(item) }}</span>
                </label>
              </div>
            </div>
          </div>

          <div class="separator" />

          <ol
            class="library-base__tabs padding-sm"
            role="tablist"
          >
            <li
              v-for="tab in libraryTabs"
              :key="tab.id"
              role="presentation"
              class="library-base__tabs__item"
              :class="activeLibraryTab === tab.id ? 'active' : ''"
            >
              <button
                :id="`library-base__tab--${tab.id}`"
                role="tab"
                :aria-selected="activeLibraryTab === tab.id ? 'true' : 'false'"
                aria-controls="library-base__body"
                class="font-weight-bold"
                :data-test="`sidebarleft-button-tab-${tab.id}`"
                @click="activeLibraryTab = tab.id"
              >
                {{ tab.label }}
              </button>
            </li>
          </ol>

          <div class="separator" />

          <div class="library-base__body padding-sm">
            <template-menu
              v-if="activeLibraryTab === 'templates'"
              ref="templateMenu"
              :search="elementMenuSearch"
              :selected-facets="templateSelectedFacets"
              @facets-loaded="handleFacetsLoaded"
              @remove-facet="removeFacet"
              @preview="handlePresetPreview"
              @used="handlePresetUsed"
            />

            <template v-else>
              <div
                v-if="recentlyUsedElementsByCategory[activeLibraryTab]"
                class="font-weight-bold margin-bottom-sm recently-used-section"
                data-test="graphics-recentlyUsedElements"
              >
                <h4 class="text-small">
                  Recently Used
                </h4>
                <div class="recent-elements">
                  <button
                    v-for="r in recentlyUsedElementsByCategory[
                      activeLibraryTab
                    ]"
                    :key="`recent-element-${r.element}`"
                    type="button"
                    class="btn btn-sm btn-icon btn-style-b"
                    :data-test="`sidebarleft-button-recentelement-${r.test}`"
                    :disabled="isDisabledElementType(r)"
                    :title="getDisabledMenuItemTitle(r)"
                    @click="pickElement(r)"
                  >
                    <span :class="r.icon" />
                    {{ r.title }}
                  </button>
                </div>
              </div>
              <div class="elements">
                <button
                  v-for="element in searchedElementOptions"
                  :key="`element-${element.key || element.element}`"
                  type="button"
                  class="element-card"
                  :data-test="`sidebarleft-button-addelement-${element.test}`"
                  :disabled="isDisabledElementType(element)"
                  :title="getDisabledMenuItemTitle(element)"
                  @click="pickElement(element)"
                >
                  <div class="element-card__icon-wrapper">
                    <div class="element-card__icon-box">
                      <template v-if="element.logo">
                        <img
                          class="element-card__logo"
                          :src="element.logo"
                          :alt="element.title"
                        />
                      </template>
                      <template v-else>
                        <span
                          class="element-card__icon"
                          :class="element.icon"
                        />
                      </template>

                      <!-- Premium bolt -->
                      <span
                        v-if="
                          (element.premium && hasUpsell(element)) ||
                            (element.limitedElement?.includes('shoppable') &&
                              !hasShoppable)
                        "
                        class="fas fa-bolt element-card__premium"
                        title="Premium Feature"
                      />
                    </div>

                    <!-- 3-dot menu -->
                    <button
                      type="button"
                      class="element-card__more btn btn-sm btn-icon btn-link"
                      data-test="sidebarleft-button-elementinfo"
                      @click.stop="toggleElementInfo(element, $event)"
                    >
                      <span class="fas fa-ellipsis-h" />
                    </button>
                  </div>

                  <div class="element-card__title">
                    {{ element.title }}
                  </div>
                </button>
              </div>
              <div
                v-if="openInfoElementData"
                class="element-card__info"
                :style="infoPopupStyle"
                @click.stop
              >
                <div class="element-card__info-title">
                  {{ openInfoElementData.title }}
                  <span class="element-card__info-meta">
                    | {{ openInfoElementData.category }}
                  </span>
                </div>
                <p
                  v-if="openInfoElementData.description"
                  class="element-card__info-desc"
                >
                  {{ openInfoElementData.description }}
                </p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </transition>

    <!-- Experience Confirmation Prompt (via Presets) -->
    <modal
      v-if="showExperiencePrompt"
      :header="true"
      :footer="true"
      :close-button="true"
      disable-click-outside
      class="experience-modal"
      data-test="graphics-experienceContentModal"
      auto-height
      @close="cancelPartnerElementSetup"
    >
      <template #modal-header>
        <h2>Setup Experience</h2>
      </template>

      <template #modal-body>
        <p class="experience-modal__description">
          Setup Experience through Partner connection?
        </p>
      </template>

      <template #modal-footer>
        <div class="display-flex items-center gap-1 flex-end">
          <button
            class="btn btn-style-b no-tooltip"
            @click="cancelPartnerElementSetup"
          >
            Skip
          </button>

          <button
            class="btn btn-style-a no-tooltip"
            @click="confirmPartnerElementSetup"
          >
            Continue to Partner
          </button>
        </div>
      </template>
    </modal>

    <experience-config-modal
      v-if="showExperienceConfig"
      :partner="selectedPartner"
      @close="closeExperienceConfigModal"
      @select="addExperienceElement"
      @reconnect="handleReconnect"
    >
    </experience-config-modal>

    <social-display-modal
      v-if="showSocialDisplayModal"
      @close="closeSocialDisplayModal"
      @select="addSocialDisplayElement"
      @create-new="createNewSocialDisplay"
    />

    <poll-display-modal
      v-if="showPollDisplayModal"
      @close="closePollDisplayModal"
      @select="addPollDisplayElement"
      @create-new="createNewPollDisplay"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import Fuse from 'fuse.js';
import { groupBy, startCase, debounce } from 'lodash-es';
import elementOptions from './elementData/index';
import ElementDefaults from '../../utils/defaults';

import socialDataModule from '../../store/modules/socialData';
import shoppableDataModule from '../../store/modules/shoppableData';
import ExperienceConfigModal from '../Modal/ExperienceConfigModal.vue';
import SocialDisplayModal from '../Modal/SocialDisplayModal.vue';
import PollDisplayModal from '../Modal/PollDisplayModal.vue';

import { postMessage, inIframe } from '../../utils/helpers';
import {
  ensurePdkQrCodeTrackingLink,
  resolveQrCodeTrackingLinkUpdate,
} from '../../utils/qrCodeTrackingLinks';
import TrackingLinks from '../../services/tracking-links';
import Modal from '../Modal/Modal.vue';

import Layers from '../Layers.vue';
import TemplateMenu from './TemplateMenu.vue';
import { formatBytes } from '../../utils/mediaSize';

const elementFuseSearchOptions = {
  threshold: 0.4,
  keys: ['title', 'description'],
};

const preferredFacetOrder = [
  'signals',
  'verticals',
  'storyTypes',
  'dataTypes',
  'layouts',
  'themes',
];

export default {
  emits: ['modal', 'update:show-element-library', 'open-google-sheets-modal', 'open-sportradar-modal', 'open-social-modal', 'open-shoppable-modal', 'open-scorestream-modal', 'open-foxforesight-modal', 'preview'],
  name: 'SidebarLeft',
  components: {
    ExperienceConfigModal,
    SocialDisplayModal,
    PollDisplayModal,
    Modal,
    Layers,
    TemplateMenu,
  },

  directives: {
    clickOutside: {
      mounted(el, binding) {
        // eslint-disable-next-line no-param-reassign
        el.clickOutsideEvent = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };

        document.addEventListener('click', el.clickOutsideEvent);
      },

      beforeUnmount(el) {
        document.removeEventListener('click', el.clickOutsideEvent);
      },
    },
  },

  props: {
    showElementLibrary: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      editing: null,
      scrollTop: 0,
      elementMenuSearch: '',
      recentElements: [],
      socialMenu: false,
      showSportradarModal: false,
      showShoppableModal: false,
      showSocialDisplayModal: false,
      showPollDisplayModal: false,
      libraryPinned: false,
      activeLibraryTab: 'templates',
      openInfoElement: null,
      activeSportradarLeague: null,
      infoPosition: {
        top: 0,
        left: 0,
      },

      showTemplateFilters: false,
      rawTemplateFacets: {},
      templateSelectedFacets: {},
      useNewPacksAndTemplates: false,

      selectedPartner: null,
      showExperiencePrompt: false,
      showExperienceConfig: false,
      // Bumped whenever the Experience flow restarts or is abandoned, so an
      // in-flight submission can tell its own flow is stale and bail out.
      experienceFlowGeneration: 0,

      flags: {
        'show-graphics-iframe-element': this.$ld.has('show-graphics-iframe-element'),
        'has-social-display-graphic-element': this.$ld.has('has-social-display-graphic-element', false),
        'has-poll-display-graphic-element': this.$ld.has('has-poll-display-graphic-element', false),
      },
    };
  },

  computed: {
    ...mapGetters('graphicsTemplate', [
      'getElementById',
      'getElementGroupMap',
      'getTemplateOwner',
      'getTemplateId',
      'getTemplateSocialData',
      'getElementCounts',
      'findElement',
      'getElements',
      'getMediaSizeSummary',
      'getMediaElements',
    ]),

    ...mapGetters([
      'hasSheetDataElement',
      'hasSportradarElement',
      'hasVideoElement',
      'isStaff',
      'overScanLimit',
      'hasShoppable',
      'hasFoxForesight',
    ]),

    ...mapState({
      partners: (state) => state.user.partners,
    }),

    owner() {
      return this.getTemplateOwner;
    },

    templateId() {
      return this.getTemplateId;
    },

    socialData() {
      return this.getTemplateSocialData;
    },

    dropdownMenuStyle() {
      return {
        // TODO: Move to CSS. Just here for testing
        position: 'fixed',
        top: 'auto',
        left: 'auto',
        right: 'auto',
        bottom: 'auto',
        marginTop: `-${this.scrollTop}px`,
      };
    },

    showVideoElement() {
      return this.hasVideoElement;
    },

    mediaSummaryTotalKnownBytes() {
      const s = this.getMediaSizeSummary;
      return s.staticImages.totalBytes + s.videos.totalBytes + s.mappedMedia.totalBytes;
    },

    mediaSummaryTotalUnknownCount() {
      const s = this.getMediaSizeSummary;
      return s.staticImages.unknownCount + s.videos.unknownCount + s.mappedMedia.unknownCount;
    },

    isHeavy() {
      return this.mediaSummaryTotalKnownBytes > 5_000_000;
    },

    libraryTabs() {
      // The Partner Apps tab is shown to ALL users.
      const baseTabs = [
        {
          id: 'templates',
          label: 'Templates',
        },
        {
          id: 'data',
          label: 'Data',
        },
        {
          id: 'basics',
          label: 'Basics',
        },
        {
          id: 'tagboard',
          label: 'Tagboard Apps',
        },
        {
          id: 'partners',
          label: 'Partner Apps',
        },
      ];

      return baseTabs;
    },

    availableElementOptions() {
      const baseOptions = elementOptions[this.activeLibraryTab]?.filter((item) => {
        if (item.ldFlag) {
          return this.flags[item.ldFlag] || false;
        }

        if (item.requiresPermission) {
          return this[item.requiresPermission] || false;
        }

        return true;
      }) || [];

      let partnerOptions = [];

      // Only add partner options to the Partners tab
      if (this.activeLibraryTab === 'partners') {
        partnerOptions = this.partners.map((partner) => ({
          key: partner.slug,
          partner: partner.slug,
          category: 'partner',

          title: partner.name,

          // Use logo property for image URLs, icon for FontAwesome classes
          ...(partner.iconUrl
            ? { logo: partner.iconUrl }
            : { icon: 'fas fa-flask-vial' }),

          description: partner.description || '',

          longDescription: '',
          faqLink: '',
          element: 'frame',
          test: 'experience',
        }));
      }

      return [...baseOptions, ...partnerOptions];
    },

    searchedElementOptions() {
      if (this.elementMenuSearch) {
        const searchResults = new Fuse(
          this.availableElementOptions,
          elementFuseSearchOptions,
        ).search(this.elementMenuSearch);

        return searchResults.map((res) => res.item);
      }

      return this.availableElementOptions;
    },

    recentlyUsedElementsByCategory() {
      return groupBy(this.recentElements, 'category');
    },

    infoPopupStyle() {
      return {
        top: `${this.infoPosition.top}px`,
        left: `${this.infoPosition.left}px`,
      };
    },

    partnerElements() {
      return this.elements?.filter((el) => !!el.partner);
    },

    elements() {
      return Object.values(this.getElements);
    },

    openInfoElementData() {
      if (!this.openInfoElement) return null;

      return (
        this.availableElementOptions.find(
          (el) => el.partner === this.openInfoElement
            || el.key === this.openInfoElement
            || el.element === this.openInfoElement,
        ) || null
      );
    },

    templateFacets() {
      return Object.entries(this.rawTemplateFacets)
        .filter(([, values]) => values.length > 0)
        .sort(([groupA], [groupB]) => {
          const aIndex = preferredFacetOrder.indexOf(groupA);
          const bIndex = preferredFacetOrder.indexOf(groupB);
          const normalizedA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
          const normalizedB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
          if (normalizedA !== normalizedB) return normalizedA - normalizedB;
          return groupA.localeCompare(groupB);
        })
        .reduce((acc, [group, values]) => {
          acc[group] = values;
          return acc;
        }, {});
    },
  },

  watch: {
    partnerElements: {
      deep: true,
      handler() {
        this.validatePartnerElement();
      },
    },
    getMediaElements: debounce(function refreshOnChange() {
      this.refreshMediaSizeSummary();
    }, 800),
  },

  async created() {
    // premade template with DDG options
    if (this.$route.name === 'create' && this.$route.query.provider) {
      const { provider } = this.$route.query;
      const providerToElement = {
        sportRadar: 'sportradar',
      };

      this.setTemplateOwner({
        value: this.$store.state.user.owner,
        options: {
          blacklist: true,
        },
      });

      this.pickDataProvider(providerToElement[provider]);
    }
  },

  mounted() {
    this.$ld.on(this.onChangeFlags);

    // Ensure PDK frames have a URL.
    // If not, will start the configure flow for a given Partner.
    this.validatePartnerElement();
    document.addEventListener('click', this.onGlobalClick);
    this.refreshMediaSizeSummary();
  },

  beforeUnmount() {
    this.$ld.off(this.onChangeFlags);
    document.removeEventListener('click', this.onGlobalClick);
    window.removeEventListener('focus', this.onFocus);
    window.removeEventListener('focus', this.onReconnectFocus);
  },

  methods: {
    ...mapActions('graphicsTemplate', [
      'addNewElement',
      'setElementGroupMapById',
      'setManyElementValuesByProp',
      'setQueuedElement',
      'setTemplateOwner',
      'takeHistorySnapshot',
      'refreshMediaSizeSummary',
    ]),

    ...mapActions(['getPartners']),

    formatBytes,

    formatMediaBucket(bucket) {
      if (!bucket) return '\u2014';
      const { totalBytes, knownCount, unknownCount } = bucket;
      const total = knownCount + unknownCount;
      if (total === 0) return '\u2014';
      if (knownCount === 0) return `${unknownCount} unknown`;
      const size = formatBytes(totalBytes);
      return unknownCount > 0 ? `${size} + ${unknownCount} unknown` : size;
    },

    // Template filter methods
    toggleTemplateFilters() {
      this.showTemplateFilters = !this.showTemplateFilters;
    },

    closeTemplateFilters() {
      this.showTemplateFilters = false;
    },

    handleFacetsLoaded(facets) {
      this.rawTemplateFacets = facets;
    },

    formatFacetLabel(facetKey) {
      if (facetKey === 'signals') return 'Highlights';
      return startCase(facetKey);
    },

    capitalizeWords(str) {
      return str
        .split(/([- ])/)
        .map(
          (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
        )
        .join('');
    },

    isFacetSelected(facetKey, value) {
      return this.templateSelectedFacets[facetKey]?.includes(value) || false;
    },

    toggleFacet(facetKey, value) {
      if (!this.templateSelectedFacets[facetKey]) {
        this.templateSelectedFacets[facetKey] = [];
      }

      const index = this.templateSelectedFacets[facetKey].indexOf(value);
      if (index > -1) {
        this.templateSelectedFacets[facetKey].splice(index, 1);
        if (this.templateSelectedFacets[facetKey].length === 0) {
          delete this.templateSelectedFacets[facetKey];
        }
      } else {
        this.templateSelectedFacets[facetKey].push(value);
      }
    },

    removeFacet({ facetKey, value }) {
      const index = this.templateSelectedFacets[facetKey]?.indexOf(value);
      if (index > -1) {
        this.templateSelectedFacets[facetKey].splice(index, 1);
        if (this.templateSelectedFacets[facetKey].length === 0) {
          delete this.templateSelectedFacets[facetKey];
        }
      }
    },

    validatePartnerElement() {
      if (this.showExperiencePrompt) {
        return;
      }

      // Check for PDK element without a set experienceId.
      // If one is found, then show the experience config modal
      // for the given partner. It's possible for an element to have no url
      // if the graphic was originally created from a template.
      // Also ignore FOX Foresight and Scorestream elements, since those are static compared
      // to dynamic partners.
      const element = this.findElement((el) => (
        !!el.partner && !el.experienceId
        && !el.isFoxForesight && !el.isScorestream
      ));

      if (!element) { return; }

      // Reopening the prompt starts a new Experience flow.
      this.experienceFlowGeneration += 1;
      this.showExperiencePrompt = true;
      this.editing = element;
    },

    confirmPartnerElementSetup() {
      this.showExperiencePrompt = false;

      if (!this.editing) {
        return;
      }

      this.setupPartnerElement(this.editing);
    },

    cancelPartnerElementSetup() {
      this.showExperiencePrompt = false;
      this.closeExperienceConfigModal();
    },

    onChangeFlags() {
      this.flags['show-graphics-iframe-element'] = this.$ld.has('show-graphics-iframe-element');
      this.flags['has-social-display-graphic-element'] = this.$ld.has('has-social-display-graphic-element', false);
      this.flags['has-poll-display-graphic-element'] = this.$ld.has('has-poll-display-graphic-element', false);
    },

    hasUpsell(item) {
      // Sheets
      if (item.element === 'sheets') {
        return !this.hasSheetDataElement;
      }

      // Video
      if (item.element === 'video') {
        return !this.hasVideoElement;
      }

      // Any Sportradar entry: generic or league-specific
      if (
        item.provider === 'sportradar'
        || item.element?.startsWith('sportradar')
      ) {
        return !this.hasSportradarElement;
      }

      return false;
    },

    isDisabledElementType(item) {
      if (!item.maxElements) {
        // No limit, so not disabled
        return false;
      }

      const limitedKeys = item.limitedElement ?? [item.element];
      const elementCount = limitedKeys.reduce(
        (memo, type) => memo + (this.getElementCounts[type] || 0),
        0,
      );

      return elementCount >= item.maxElements;
    },

    getDisabledMenuItemTitle(item) {
      return this.isDisabledElementType(item)
        ? 'Only one of this element type is allowed per graphic template.'
        : '';
    },

    async onFocus() {
      // Refetch partners, and check if we now have tokens for the configured partner
      const partners = await this.getPartners();
      const partner = partners.find((rec) => rec.slug === this.selectedPartner);
      if (partner?.tokens) {
        // Unbind the focus event, and show the experience config modal
        window.removeEventListener('focus', this.onFocus);
        this.showExperienceConfig = true;
      }
    },

    closeExperienceConfigModal() {
      // Closing abandons any submission still in flight.
      this.experienceFlowGeneration += 1;
      this.showExperienceConfig = false;
      this.selectedPartner = null;

      // Clean up any pending reconnect/connect focus listeners
      window.removeEventListener('focus', this.onFocus);
      window.removeEventListener('focus', this.onReconnectFocus);

      // If we were editing an existing element, then remove `partner` from that element.
      // That way, the user can edit the frame URL directly.
      if (this.editing) {
        const payload = {
          partner: null,
          experienceId: null,
        };

        if (this.editing.type === 'qrcode') {
          payload.permalink = 'https://tagboard.com';
        } else if (this.editing.type === 'frame') {
          payload.url = 'https://tagboard.com';
        }

        this.setManyElementValuesByProp({
          value: {
            id: this.editing.id,
            pathValueMap: payload,
          },
        });

        this.editing = null;
      }
    },

    handleReconnect() {
      // Close the modal, but keep selectedPartner so we re-open after reconnect
      this.showExperienceConfig = false;

      const partner = this.partners.find(
        (rec) => rec.slug === this.selectedPartner,
      );

      if (!partner?.oauthUrl) {
        return;
      }

      // Open OAuth popup for re-authentication
      window.open(
        partner.oauthUrl,
        '_blank',
        'popup=true,noopener,width=600,height=600',
      );

      // After popup closes and user returns to this window, check for new tokens
      window.addEventListener('focus', this.onReconnectFocus);
    },

    async onReconnectFocus() {
      const partners = await this.getPartners();
      const partner = partners.find(
        (rec) => rec.slug === this.selectedPartner,
      );

      if (partner?.tokens) {
        window.removeEventListener('focus', this.onReconnectFocus);
        this.showExperienceConfig = true;
      }
    },

    async addExperienceElement(opts = {}) {
      const {
        name, experienceId, url, qrCodeUrl,
      } = opts;

      // A submission owns this generation: a prompt reopen, a modal close, or a
      // newer submission invalidates it while the ensure request is in flight.
      this.experienceFlowGeneration += 1;
      const flowGeneration = this.experienceFlowGeneration;
      const { editing: editingElement, selectedPartner } = this;
      const isCurrentFlow = () => (
        flowGeneration === this.experienceFlowGeneration
        && this.editing === editingElement
      );

      this.showExperienceConfig = false;
      this.$emit('update:show-element-library', false);

      if (editingElement) {
        const payload = {
          experienceId,
          partner: selectedPartner,
          name,
        };
        const editingQrCode = editingElement.type === 'qrcode';
        const updateQrCode = !!qrCodeUrl && (editingQrCode || !url);

        // Keep the element's existing destination behavior: frames get `url`,
        // QR codes get `permalink` (the partner destination scans should reach).
        if (updateQrCode) {
          payload.permalink = qrCodeUrl;
        } else if (url) {
          payload.url = url;
        }

        if (updateQrCode) {
          // Capture the destination before the store update so a failed ensure
          // can tell whether the element's existing rid is now stale.
          const previousPermalink = editingElement.permalink;
          const currentTrackingLinkRid = editingElement.trackingLinkRid;
          const result = await this.ensureQrCodeTrackingLink(
            qrCodeUrl,
            name,
            currentTrackingLinkRid,
          );

          // An older flow must not write its result over the active one.
          if (!isCurrentFlow()) { return; }

          Object.assign(payload, resolveQrCodeTrackingLinkUpdate({
            result,
            destinationUrl: qrCodeUrl,
            previousDestinationUrl: previousPermalink,
            currentTrackingLinkRid,
          }));
        }

        // Update existing Experience element instead of creating a new one.
        this.setManyElementValuesByProp({
          value: {
            id: editingElement.id,
            pathValueMap: payload,
          },
        });

        this.editing = null;
        return;
      }

      if (url && qrCodeUrl) {
        const { rid: trackingLinkRid } = await this.ensureQrCodeTrackingLink(qrCodeUrl, name);
        if (!isCurrentFlow()) { return; }

        const qrCodeSettings = {
          name: `[QR Code] ${name}`,
          permalink: qrCodeUrl,
          experienceId,
          partner: selectedPartner,
        };

        if (trackingLinkRid) {
          qrCodeSettings.trackingLinkRid = trackingLinkRid;
        }

        // Add as group
        // Just add them directly, since addElementOnClick doesn't play well with a
        // sequence of elements.
        await Promise.all([
          this.addNewElement({
            value: {
              type: 'frame',
              settings: {
                name: `[View] ${name}`,
                url,
                experienceId,
                partner: selectedPartner,
              },
            },
            options: {
              blacklist: true,
            },
          }),

          this.addNewElement({
            value: {
              type: 'qrcode',
              settings: qrCodeSettings,
            },
            options: {
              blacklist: true,
            },
          }),
        ]);

        return;
      }

      if (url) {
        this.addElementOnClick('frame', {
          settings: {
            name,
            url,
            experienceId,
            partner: selectedPartner,
          },
        });
      }

      if (qrCodeUrl) {
        const { rid: trackingLinkRid } = await this.ensureQrCodeTrackingLink(qrCodeUrl, name);
        if (!isCurrentFlow()) { return; }

        const settings = {
          name,
          permalink: qrCodeUrl,
          experienceId,
          partner: selectedPartner,
        };

        if (trackingLinkRid) {
          settings.trackingLinkRid = trackingLinkRid;
        }

        this.addElementOnClick('qrcode', { settings });
      }
    },

    /**
     * Ensure a PDK Experience QR code has a TrackingLink for its destination.
     * Never throws; a failure leaves the element without a rid so the existing
     * save-time missing-tracking-link warning still applies.
     * Resolves to `{ rid, status }` — see `ensurePdkQrCodeTrackingLink`.
     */
    ensureQrCodeTrackingLink(qrCodeUrl, experienceName, currentTrackingLinkRid) {
      return ensurePdkQrCodeTrackingLink({
        destinationUrl: qrCodeUrl,
        partnerSlug: this.selectedPartner,
        experienceName,
        currentTrackingLinkRid,
        createOrReuseTrackingLink: TrackingLinks.createOrReuseTrackingLink,
      });
    },

    setupPartnerElement(element) {
      // Trigger the connection flow if not already connected.
      const partner = this.partners.find((rec) => rec.slug === element.partner);
      if (!partner) {
        return;
      }

      this.selectedPartner = partner.slug;

      if (!partner.tokens) {
        // Showing the oauth connection flow.
        window.open(
          partner.oauthUrl,
          '_blank',
          'popup=true,noopener,width=600,height=600',
        );

        window.addEventListener('focus', this.onFocus);
        return;
      }

      // Show modal for configuring the Experience
      this.showExperienceConfig = true;
    },

    handleSportradarClose() {
      this.showSportradarModal = false;
      this.activeSportradarLeague = null;
    },

    async pickDataProvider(source, options = {}) {
      const { leagueName } = options;

      if (source === 'sheets') {
        if (!this.hasSheetDataElement) {
          this.$store.dispatch('setPermissionWarning', 'sheet-data');
          return;
        }

        const selectedIds = this.$store.state.templates.selectedElements || [];
        const selectedEl = selectedIds[0] && this.getElementById(selectedIds[0]);

        if (!selectedEl || selectedEl.type !== 'sheet-data') {
          const configs = {
            sheets: {
              ...ElementDefaults['sheet-data'],
              name: 'Google Sheet',
              type: 'sheet-data',
              data: {
                isConnected: false,
              },
            },
          };

          const element = await this.addNewElement({
            value: {
              type: 'data',
              settings: {
                source,
                ...configs[source],
              },
            },
            options: {
              blacklist: true,
            },
          });

          if (element && element.type === 'sheet-data') {
            await this.setElementGroupMapById({
              value: {
                id: element.id,
                childIds: [],
              },
              options: {
                blacklist: true,
              },
            });

            this.$store.dispatch('selectElements', [element.id]);
            this.$store.dispatch(`${element.id}/setForceConnect`, true);
          }
        }

        this.$emit('open-google-sheets-modal');
        return;
      }

      if (source === 'sportradar' && !this.hasSportradarElement) {
        this.$store.dispatch('setPermissionWarning', 'sportradar');
        return;
      }

      if (source === 'sportradar') {
        this.$emit('open-sportradar-modal', { leagueName: leagueName || null });
      }
    },

    pickElement(elData) {
      const { element } = elData;

      if (elData.category === 'partner' && elData.partner) {
        this.setupPartnerElement(elData);
        return;
      }

      switch (element) {
        case 'image':
        case 'video':
          this.pickAsset(element);
          break;

        case 'social':
          this.triggerSocialMenu(elData);
          return;

        case 'social-display':
          this.triggerSocialDisplayModal();
          return;

        case 'poll-display':
          this.triggerPollDisplayModal();
          return;

        case 'sheets':
          this.pickDataProvider('sheets');
          break;

        case 'scorestream':
          this.triggerScorestreamModal();
          return;

        case 'foxforesight':
          this.triggerFoxForesightModal();
          return;

        case 'sportradar': // fallback generic, if you still have it
          this.pickDataProvider('sportradar');
          break;

        default:
          // League-specific Sportradar elements: use their leagueName
          if (elData.provider === 'sportradar' && elData.leagueName) {
            this.pickDataProvider('sportradar', {
              leagueName: elData.leagueName,
            });
            break;
          }

          // rest of your existing cases:
          switch (element) {
            case 'oval':
            case 'rectangle':
            case 'triangle':
            case 'qrcode':
              this.addElementOnClick(element);
              break;
            case 'slideshow':
              this.addElementOnClick(element, { isGroup: true });
              break;
            case 'marquee':
              this.addElementOnClick(element, {
                isGroup: true,
                skipGroupRecalculation: true,
              });
              break;
            case 'shoppable':
              this.triggerShoppableModal();
              break;
            default:
              // Pass any element defaults overrides (e.g. a preset timerType
              // for the Countdown/Stopwatch variants) through to creation.
              this.addElementOnClick(
                element,
                elData.settings ? { settings: elData.settings } : {},
              );
              break;
          }
          break;
      }

      this.recentElements = this.recentElements.filter(
        (el) => (el.key || el.element) !== (elData.key || elData.element),
      );

      if (this.recentElements.length > 2) {
        this.recentElements.pop();
      }

      this.recentElements.unshift(elData);

      if (!this.libraryPinned) {
        this.toggleElementLibrary();
      }
    },

    onScroll(e) {
      this.scrollTop = e.target.scrollTop;
    },

    getLayerType(element) {
      if (element.type === 'group') {
        return 'group-layer';
      }

      return 'layer';
    },

    async addElementOnClick(addType, elOptions = {}, options = {}) {
      const {
        settings, parentId, skipGroupRecalculation, isGroup,
      } = elOptions;

      this.setQueuedElement({
        value: {
          value: {
            type: addType,
            settings,
            parentId,
            isGroup,
            skipGroupRecalculation,
          },
          options,
        },
      });

      // Only show modal when over limit
      if (addType === 'qrcode' && this.overScanLimit) {
        this.$store.dispatch('setShowScanLimitWarning', true);
      }
    },

    pickAsset(type) {
      if (type === 'video' && !this.hasVideoElement) {
        // Show upsell modal instead
        this.$store.dispatch('setPermissionWarning', 'video');
        return;
      }

      this.$emit('modal', { open: true, type });

      window.assetmanfn = ({ asset, meta: additiveMeta = {} }) => {
        const safeAsset = asset || {};
        const hasMeta = Boolean(safeAsset.meta);
        const assetMeta = safeAsset.meta;
        const settings = {
          name: safeAsset.title || '',
          styles: {},
        };

        if (safeAsset.asset_type === 'video') {
          settings.src = safeAsset.media_url || '';
          settings.thumbnailUrl = safeAsset.thumbnail_url || '';
          settings.posterUrl = safeAsset.thumbnail_url || '';

          if (hasMeta) {
            settings.styles.width = assetMeta.width;
            settings.styles.height = assetMeta.height;
            settings.duration = assetMeta.duration;
            settings.contentType = assetMeta.content_type;
          }
        } else if (safeAsset.asset_type === 'image') {
          settings.imageUrl = safeAsset.media_url || '';

          if (hasMeta) {
            settings.styles.width = assetMeta.width;
            settings.styles.height = assetMeta.height;
            settings.imageMeta = additiveMeta;
          }
        }

        this.addElementOnClick(safeAsset.asset_type || 'image', {
          settings,
        });

        this.$emit('modal', { open: false });
      };
    },

    handlePresetUsed() {
      if (!this.libraryPinned) {
        this.toggleElementLibrary();
      }
    },

    triggerSocialMenu(element) {
      this.$emit('open-social-modal');

      // prevent duplicates in recents
      this.recentElements = this.recentElements.filter(
        (el) => el.element !== element.element,
      );

      if (this.recentElements.length > 2) {
        this.recentElements.pop();
      }

      this.recentElements.unshift(element);
    },

    triggerShoppableModal() {
      this.$emit('open-shoppable-modal');
    },

    triggerScorestreamModal() {
      this.$emit('open-scorestream-modal');
    },

    triggerSocialDisplayModal() {
      this.showSocialDisplayModal = true;
    },

    triggerFoxForesightModal() {
      this.$emit('open-foxforesight-modal');
    },

    closeSocialDisplayModal() {
      this.showSocialDisplayModal = false;
    },

    addSocialDisplayElement(display) {
      this.showSocialDisplayModal = false;

      this.addElementOnClick('social-display', {
        settings: {
          name: display.title || 'Social Graphic',
          displayId: display._id,
          layout: display.layout,
          isSocialDisplay: true,
        },
      });

      if (!this.libraryPinned) {
        this.$emit('update:show-element-library', false);
      }
    },

    createNewSocialDisplay() {
      this.showSocialDisplayModal = false;

      if (inIframe()) {
        postMessage('create-social-display');
      }

      if (!this.libraryPinned) {
        this.$emit('update:show-element-library', false);
      }
    },

    triggerPollDisplayModal() {
      this.showPollDisplayModal = true;
    },

    closePollDisplayModal() {
      this.showPollDisplayModal = false;
    },

    addPollDisplayElement(display) {
      this.showPollDisplayModal = false;

      this.addElementOnClick('poll-display', {
        settings: {
          name: display.title || 'Poll Graphic',
          displayId: display.displayId,
          pollId: display.pollId,
          isPollDisplay: true,
          currentRoundIndex: 0,
          currentDisplayState: 'results',
          currentQuestionIndex: 0,
        },
      });

      if (!this.libraryPinned) {
        this.$emit('update:show-element-library', false);
      }
    },

    createNewPollDisplay() {
      this.showPollDisplayModal = false;

      if (inIframe()) {
        postMessage('create-poll-display');
      }

      if (!this.libraryPinned) {
        this.$emit('update:show-element-library', false);
      }
    },

    handlePresetPreview(preset) {
      this.$emit('preview', preset);
    },

    async addShoppableElement(selectedElements) {
      const namespace = crypto.randomUUID();

      this.$store.registerModule(namespace, shoppableDataModule);
      await this.$store.dispatch(`${namespace}/setNamespace`, namespace);

      this.$store.dispatch(`${namespace}/createShoppableTemplate`, {
        selectedElements,
      });
    },

    addScorestreamElement({ widgetType, widgetId }) {
      const baseUrl = process.env.GRAPHICS_SPA;
      const url = `${baseUrl}/scorestream.html?widgetType=${encodeURIComponent(
        widgetType,
      )}&widgetId=${encodeURIComponent(widgetId)}`;

      this.addElementOnClick('frame', {
        settings: {
          name: 'ScoreStream Widget',
          url,
          widgetType,
          widgetId,
          isScorestream: true,
          partner: 'scorestream',
        },
      });

      // Close the element library if it's not pinned
      if (!this.libraryPinned) {
        this.$emit('update:show-element-library', false);
      }
    },

    addFoxForesightElement(config) {
      const params = new URLSearchParams();

      // Add required parameters with API-expected names
      params.append('chart_type', config.chartType);
      params.append('game_id', config.game);

      // Add all additional parameters from config
      // These come from the dynamic form and should match API param names
      Object.keys(config).forEach((key) => {
        if (!['chartType', 'date', 'game'].includes(key) && config[key]) {
          params.append(key, config[key]);
        }
      });

      const url = `${process.env.MONO_BASE}/partners/sportsgold/render?${params.toString()}`;

      // Build settings object with all config values
      const settings = {
        name: 'Fox Foresight Chart',
        imageUrl: url,
        chartType: config.chartType,
        date: config.date,
        game: config.game,
        isFoxForesight: true,
        partner: 'fox-foresight',
      };

      // Add all other config properties to settings
      Object.keys(config).forEach((key) => {
        if (!['chartType', 'date', 'game'].includes(key)) {
          settings[key] = config[key];
        }
      });

      this.addElementOnClick('image', { settings });

      // Close the element library if it's not pinned
      if (!this.libraryPinned) {
        this.$emit('update:show-element-library', false);
      }
    },

    deselectElement() {
      this.$store.dispatch('setTextEditing', false);
      this.$store.dispatch('selectElements', []);
    },

    createSocialElement(socialTemplate) {
      this.socialMenu = false;
      const namespace = crypto.randomUUID();

      this.$store.registerModule(namespace, socialDataModule);
      this.$store.dispatch(`${namespace}/setNamespace`, namespace);

      this.$store.dispatch(`${namespace}/createSocialTemplate`, {
        socialTemplateType: socialTemplate.template,
        namespace,
        contentType: socialTemplate.option,
      });
    },

    getBalloonPosition() {
      return this.showVideoElement ? 'up-left' : 'up-right';
    },

    hasElements() {
      return this.elements && this.elements.length > 0;
    },

    openElementLibrary() {
      if (!this.showElementLibrary) {
        this.$emit('update:show-element-library', true);
      }
    },

    toggleElementLibrary(force = false) {
      if (!this.libraryPinned || force) {
        this.$emit('update:show-element-library', !this.showElementLibrary);
      }
    },

    toggleElementInfo(element, event) {
      const id = element.partner || element.key || element.element;

      if (this.openInfoElement === id) {
        this.openInfoElement = null;
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const padding = 8;

      this.infoPosition = {
        top: rect.bottom + padding,
        left: Math.max(rect.left - 20, 8),
      };

      this.openInfoElement = id;
    },

    onGlobalClick() {
      if (this.openInfoElement) {
        this.openInfoElement = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.sidebar-wrapper {
  height: 100%;
  display: flex;
  max-width: 550px; // sidebar (250) + element library (300)
  z-index: 3;
}

.sidebar {
  min-width: 250px;
  display: flex;
  flex-direction: column;

  &__top {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  &__bottom {
    flex: 0 0 auto;
  }
}

.element-library {
  position: relative;
  z-index: 1;
  width: 315px;
  min-width: 0;
  border-right: 1px solid #dfeaef;
  border-bottom: 1px solid #dfeaef;
  background-color: #f9f9f9;
  height: calc(100vh - 95px);
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.sidebar__layers-group {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;

  .sidebar-form-group--full {
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .sidebar__layers {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0.5rem 0;
  }

  .sidebar__add-element-cta {
    display: flex;
    justify-content: center;

    button {
      width: 55%;
      border: none;
    }
  }
}

.media-size-summary {
  font-size: 12px;
  color: #555;

  &__pill {
    font-size: 10px;
    padding: 1px 6px;
    font-weight: 400;
    border-radius: 30px;
    border: 1px solid;
    margin-right: 6px;
    white-space: nowrap;

    &--heavy {
      color: #8C221C;
      border-color: #B32B23;
      background-color: #FFF5F5;
    }

    &--optimal {
      color: #136C34;
      border-color: #188A42;
      background-color: #F4FCF7;
    }
  }

  &__total {
    font-size: 16px;
    font-weight: 700;
    color: #000;
    margin-bottom: 6px;
  }

  &__subtext {
    font-size: 8px;
    font-weight: 400;
    color: #64748b;
  }

  &__row {
    gap: 8px;
  }

  &__warning {
    margin-top: 8px;
    padding: 6px 8px;
    border-radius: 4px;
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
    font-size: 11px;
    line-height: 1.4;
  }
}

.library-base {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #fff;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem;
  }

  &__tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    list-style: none;
    margin: 0;

    &__item {
      button {
        background: transparent;
        cursor: pointer;
        padding: 0.25rem 1rem;
        border: 1px solid #6c8396;
        color: #6c8396;
        border-radius: 30px;

        &:hover,
        &:focus {
          background: #dff7fb;
          color: #334155;
        }
      }

      &.active {
        button {
          background: #00b4cc;
          color: #fff;
          border-color: #00b4cc;
        }
      }
    }
  }

  &__body {
    flex: 1;
    overflow: auto;
    position: relative;
  }
}

.separator {
  height: 1px;
  width: 100%;
  border-bottom: 1px solid #dfeaef;
}

.recent-elements {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;

  button {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 3px 6px;
    margin: 0;

    span {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.25em;
      height: 1.25em;
      line-height: 1;
      flex: 0 0 1.25em;
    }
  }
}

.recently-used-section {
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 0.8rem;
  background: #f1f5f9;

  h4 {
    margin-top: 0;
    font-weight: 800;
  }
}

/* 3 cards per row */
.elements {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  padding-bottom: 20px; // room to scroll last-row popups into view
}

.element-card {
  position: relative;
  border: none;
  border-radius: 6px;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__icon-wrapper {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-start;
  }

  &__icon-box {
    width: 100%;
    max-width: 110px;
    aspect-ratio: 1 / 1;
    border-radius: 12px;
    border: 1px solid #dfeaef;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 3px 5px rgba(0, 0, 0, 0.02), 0 0 2px rgba(0, 0, 0, 0.05),
      0 1px 4px rgba(0, 0, 0, 0.08);
    transition: transform 0.16s ease-out, box-shadow 0.16s ease-out,
      border-color 0.16s ease-out, background-color 0.16s ease-out;
    overflow: hidden;
  }

  &__icon {
    font-size: 28px;
    line-height: 1;
    transition: transform 0.16s ease-out;
  }

  &__logo {
    max-width: 70%;
    max-height: 65%;
    object-fit: contain;
    display: block;
  }

  &__premium {
    position: absolute;
    top: 6px;
    left: 8px;
    font-size: 12px;
    color: #fbbf24;
  }

  &__more {
    position: absolute;
    top: 2px;
    right: 3px;
    opacity: 0;
    background: transparent;
    padding: 3px 3px 1px;
    border: 1px solid transparent;
    border-radius: 6px;

    &:focus,
    &:hover {
      background: rgba(255, 255, 255, 0.5);
      border: 1px solid #dfeaef;
    }

    .fa-ellipsis-h {
      font-size: 14px;
    }
  }

  &__title {
    margin-top: 6px;
    font-weight: 600;
    font-size: 13px;
    line-height: 1.3;
    max-width: 110px;
  }

  &__info {
    position: fixed;
    z-index: 9999;
    min-width: 180px;
    max-width: 260px;
    padding: 8px 10px;
    border-radius: 8px;
    background: #ffffff;
    color: #0f172a;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 25px rgba(15, 23, 42, 0.16),
      0 4px 8px rgba(15, 23, 42, 0.08);
    text-align: left;
  }

  &__info-title {
    font-weight: 600;
    font-size: 13px;
    margin-bottom: 4px;
    color: #0f172a;
  }

  &__info-meta {
    font-size: 11px;
    color: #64748b;
    text-transform: capitalize;
  }

  &__info-label {
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__info-desc {
    margin: 0;
    font-size: 12px;
    line-height: 1.4;
    color: #475569;
  }

  &:hover .element-card__icon-box,
  &:focus-within .element-card__icon-box {
    transform: translateY(-2px) scale(1.03);
    border-color: #00b4cc;
    background-color: #f0fdff;
    box-shadow: 0 10px 18px rgba(15, 23, 42, 0.18),
      0 4px 6px rgba(15, 23, 42, 0.14);
  }

  &:hover .element-card__icon,
  &:focus-within .element-card__icon,
  &:hover .element-card__logo,
  &:focus-within .element-card__logo {
    transform: translateY(-1px) rotate(-4deg);
    color: #00b4cc;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    line-height: 1;
  }

  &__logo {
    display: block;
    max-width: 72%;
    max-height: 72%;
    object-fit: contain;
  }

  &:hover .element-card__more,
  &:focus-within .element-card__more {
    opacity: 1;
  }
}

input {
  border-radius: 0 6px 6px 0;
}

input:focus,
textarea:focus,
.input:focus,
.textarea:focus {
  background-color: #f2f6fa;
  border-color: rgba(0, 180, 204, 0.5);
  box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075),
    0 0 8px rgba(0, 180, 204, 0.6);
  outline: 0;
}

.fa-thumbtack {
  transition: transform 0.25s ease-in-out;
}

.btn-link:focus,
.btn-link:hover {
  box-shadow: none;
  outline: none;
  color: #0095a9;
}

.element-search {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
}

.element-search__input {
  flex: 1;
  padding-left: 3rem;
  border-radius: 2rem;
  height: 34px;
  font-size: 13px;
}

.element-search__icon {
  position: absolute;
  left: 2rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.2rem;
  color: #64748b;
  pointer-events: none;
}

.element-search__filter-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #dfeaef;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover,
  &:focus {
    background: #f0fdff;
    border-color: #00b4cc;
    color: #00b4cc;
    outline: none;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.15);
  }

  .fa-bars-filter {
    font-size: 14px;
  }
}

.template-filters-dropdown {
  position: absolute;
  background: #ffffff;
  border: 1px solid #dfeaef;
  border-radius: 6px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.12),
    0 4px 8px rgba(15, 23, 42, 0.08);
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  z-index: 10000;
  min-width: 220px;
  max-width: 280px;
  top: 48px;
  right: 8px;
  left: auto;
}

.facet-group {
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
}

.facet-group-label {
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #64748b;
  letter-spacing: 0.05em;
}

.facet-option {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: #f8fafc;
  }

  input[type="checkbox"] {
    margin-right: 8px;
    margin-top: 0;
    margin-bottom: 0;
    cursor: pointer;
    flex-shrink: 0;
  }

  span {
    flex: 1;
    line-height: 1;
    padding-top: 1px;
  }
}
</style>
